import { nfp } from 'components/MainComponent/textBuilder'
import { scaleLinear, extent } from 'd3'
import { useMemo, useRef } from 'react'
import styled from 'styled-components'
import { useResizeObserver } from 'usehooks-ts'

const BarHeight = 40
const TickCount = 5
const MinStep = 1
const HealthChart = ({
  lang,
  data = [
    [0, 1],
    [1, 2],
    [2, 3],
    [3, 4],
    [4, 5],
    [5, 6],
    [6, 7],
    [7, 8]
  ],
  svgHeight = 200
}: {
  lang: 'de' | 'fr' | undefined
  data?: number[][]
  svgHeight?: number
}) => {
  const ref = useRef<HTMLDivElement>(null)
  const { width = 0 } = useResizeObserver({
    ref,
    box: 'border-box'
  })
  const plotArea = useMemo(
    () => ({
      left: 120,
      top: 30,
      right: width,
      bottom: svgHeight - 20
    }),
    [width]
  )

  const xDomain = useMemo(() => extent(data, d => d[1] || 0), [data]) as [
    number,
    number
  ]
  const plotWidth = useMemo(
    () => Math.max(0, plotArea.right - plotArea.left),
    [plotArea.left, plotArea.right]
  )

  const xScale = useMemo(() => {
    return scaleLinear<number>()
      .domain([0, Math.max(500, xDomain[1] + 50)])
      .range([0, plotWidth])
  }, [plotWidth, xDomain])
  return (
    <ChartContainer ref={ref} $svgHeight={svgHeight} className='health-chart'>
      <ChartSVG>
        <defs>
          <marker
            id='arrow'
            viewBox='0 0 10 10'
            markerWidth='5'
            markerHeight='5'
            refX='8'
            refY='5'
            orient='auto'
            fill='var(--text-color)'
          >
            <polygon points='0 0 10 5 0 10' />
          </marker>
        </defs>
        <g
          key={1}
          transform={`translate(${plotArea.left}, ${plotArea.top + 20})`}
        >
          <ChartBar
            x={0}
            y={0}
            width={xScale.range()[1]}
            height={BarHeight}
            fill='var(--gray-bar-bg)'
          />
          <ChartBar
            x={0}
            y={0}
            width={xScale(data[0][1])}
            height={BarHeight}
            fill='var(--orange-main)'
          />
          <TickText
            x={xScale(data[0][1]) - 5}
            y={BarHeight / 2 + 5}
            textAnchor='end'
            fill='white'
          >
            {Math.round(data[0][1])}
          </TickText>
        </g>
        <AxisLabel
          textAnchor='start'
          x={0}
          y={plotArea.top + BarHeight / 2 + 25}
          fill='var(--text-color)'
        >
          {lang === 'de' ? 'Sie' : 'Vous'}
        </AxisLabel>
        <AxisLabel
          textAnchor='start'
          x={0}
          y={plotArea.top + BarHeight * 1.75 + 25}
          fill='var(--text-color)'
        >
          {lang === 'de' ? 'Durchschnitt' : 'Moyenne'}
        </AxisLabel>

        <g
          key={2}
          transform={`translate(${plotArea.left}, ${
            plotArea.top + BarHeight + 30
          })`}
        >
          <ChartBar
            x={0}
            y={0}
            width={xScale.range()[1]}
            height={BarHeight}
            fill='var(--gray-bar-bg)'
          />
          <ChartBar
            x={0}
            y={0}
            width={xScale(data[1][1])}
            height={BarHeight}
            fill='var(--coffee-color_3)'
          />
          <TickText
            x={xScale(data[1][1]) - 5}
            y={BarHeight / 2 + 5}
            textAnchor='end'
            fill='white'
          >
            {Math.round(data[1][1])}
          </TickText>
        </g>
        <g>
          <line
            x1={plotArea.left + xScale(data[2][1])}
            x2={plotArea.left + xScale(data[2][1])}
            y1={0}
            y2={plotArea.bottom}
            stroke='var(--green-main)'
            strokeWidth='1.5'
          />
          <TickText
            x={plotArea.left + xScale(data[2][1]) - 8}
            y={20}
            textAnchor='end'
            fill='var(--green-main)'
            style={{ fontWeight: 700 }}
          >
            {lang === 'de'
              ? 'Grenzwert für Ihr Gewicht:'
              : 'Limite pour votre poids:'}{' '}
            {Math.round(data[2][1])} mg
          </TickText>
        </g>
      </ChartSVG>
    </ChartContainer>
  )
}
const ChartContainer = styled.div<{ $svgHeight: number }>`
  width: 100%;
  height: ${({ $svgHeight }) => $svgHeight}px;
`
const ChartSVG = styled.svg`
  width: 100%;
  height: 100%;
`

const ChartBar = styled.rect`
  transition: y 0.4s ease-in-out, height 0.4s ease-in-out;
`

const TickText = styled.text`
  font-size: 14px;
  font-family: var(--font-sans-serif);
`

const AxisLabel = styled.text`
  font-size: 15px;
  font-family: var(--font-sans-serif);
`
export default HealthChart
